/* ============================================================
 * NetworkCoreService
 * ------------------------------------------------------------
 * Servicio centralizado para manejar la conectividad de red.
 * Detecta si el dispositivo está online u offline y notifica
 * a toda la aplicación cuando hay cambios.
 *
 * - Usa el plugin @capacitor/network.
 * - Expone un observable (isOnline$) para que otros servicios,
 *   como SyncCoreService o PendingCoreService, reaccionen.
 * - Evita intentos de sincronización cuando no hay conexión.
 * ============================================================ */

import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Network } from '@capacitor/network';

@Injectable({
  providedIn: 'root'
})
export class NetworkCoreService {
  // 🔹 Observable global que mantiene el estado actual de la red (true = online, false = offline)
  private isOnlineSubject = new BehaviorSubject<boolean>(true);
  isOnline$ = this.isOnlineSubject.asObservable();

  constructor() {
    // Al iniciar, obtenemos el estado actual de la red
    this.initNetworkListener();
  }

  /* ============================================================
   * 🔹 Inicializa el listener de red
   * Escucha cambios en la conexión (online/offline) en tiempo real.
   * ============================================================ */
  private async initNetworkListener(): Promise<void> {
    try {
      // Consulta el estado inicial
      const status = await Network.getStatus();
      this.isOnlineSubject.next(status.connected);
      console.log(`[Network] Estado inicial: ${status.connected ? 'Online' : 'Offline'}`);

      // 🔹 Suscripción al evento de cambios de red
      Network.addListener('networkStatusChange', (status) => {
        this.isOnlineSubject.next(status.connected);
        console.log(`[Network] Estado cambiado: ${status.connected ? 'Online' : 'Offline'}`);
      });
    } catch (error) {
      console.error('[Network] Error al inicializar listener:', error);
    }
  }

  /* ============================================================
   * 🔹 Verifica el estado actual de conexión
   * Retorna un valor booleano actual (sin necesidad de observar cambios).
   * ============================================================ */
  async isOnline(): Promise<boolean> {
    try {
      const status = await Network.getStatus();
      this.isOnlineSubject.next(status.connected);
      return status.connected;
    } catch (error) {
      console.error('[Network] Error al obtener estado de red:', error);
      return false;
    }
  }

  /* ============================================================
   * 🔹 Forzar actualización manual del estado
   * Útil si un módulo necesita verificar conexión antes de operar.
   * ============================================================ */
  async refreshStatus(): Promise<void> {
    const connected = await this.isOnline();
    this.isOnlineSubject.next(connected);
    console.log(`[Network] Estado actualizado manualmente: ${connected ? 'Online' : 'Offline'}`);
  }
}
